<template>
	<v-container>
		<PageTitle
		v-if="!embedded"
		icon="mdi-bank"
		main="Banco de Horas"
		sub="Controle de variacao do periodo e saldo acumulado."
		/>
		<v-alert dense text type="info" class="mb-4 clean-alert">
			<strong>Variacao do periodo</strong> vem dos expedientes.
			<strong>Banco acumulado</strong> vem do saldo gravado.
			<strong>Horas nao lancadas</strong> sao apenas conferencia administrativa.
		</v-alert>

		<v-row class="mb-4" dense>
			<v-col cols="12" sm="6" md="3">
				<v-card flat class="summary-card">
					<v-card-text class="pa-4">
						<Kpi
						:value="bancoHoras.length"
						label="Colaboradores"
						icon="mdi-account-group"
						color="secondary"
						/>
					</v-card-text>
				</v-card>
			</v-col>

			<v-col cols="12" sm="6" md="3">
				<v-card flat class="summary-card">
					<v-card-text class="pa-4">
						<Kpi
						:value="colaboradoresCreditoPeriodo"
						label="Com credito"
						icon="mdi-trending-up"
						color="success"
						/>
					</v-card-text>
				</v-card>
			</v-col>

			<v-col cols="12" sm="6" md="3">
				<v-card flat class="summary-card">
					<v-card-text class="pa-4">
						<Kpi
						:value="colaboradoresDebitoPeriodo"
						label="Com debito"
						icon="mdi-alert-circle-outline"
						color="error"
						/>
					</v-card-text>
				</v-card>
			</v-col>

			<v-col cols="12" sm="6" md="3">
				<v-card flat class="summary-card">
					<v-card-text class="pa-4">
						<Kpi
						:value="formatMinutes(saldoAcumuladoGeralMin)"
						label="Banco acumulado geral"
						icon="mdi-scale-balance"
						:color="saldoAcumuladoGeralMin < 0 ? 'error' : saldoAcumuladoGeralMin > 0 ? 'success' : 'secondary'"
						/>
					</v-card-text>
				</v-card>
			</v-col>
		</v-row>

		<v-card flat class="main-card">
			<v-card-title class="secondary--text font-weight-regular pb-2 table-title-wrap">
				<div class="d-flex align-center title-block">
					<v-icon left color="secondary" class="mr-2">mdi-account-clock</v-icon>
					<div>
						<div class="text-subtitle-1 font-weight-medium">Consulta por colaborador</div>
						<div class="text-caption grey--text text--darken-1">
							A tabela separa variacao do periodo, saldo gravado e auditoria de horas nao lancadas.
						</div>
					</div>
				</div>

				<v-spacer />

				<div class="toolbar-actions">
					<v-select
					v-model="mesSelecionado"
					:items="meses"
					item-text="nome"
					item-value="numero"
					dense
					outlined
					hide-details
					label="Mes"
					class="period-select"
					@change="loadBancoHoras"
					/>

					<v-select
					v-model="anoSelecionado"
					:items="anos"
					dense
					outlined
					hide-details
					label="Ano"
					class="year-select"
					@change="loadBancoHoras"
					/>

					<v-text-field
					v-model="search"
					dense
					outlined
					hide-details
					clearable
					prepend-inner-icon="mdi-magnify"
					label="Buscar colaborador"
					class="table-search"
					/>

					<v-btn
					color="primary"
					outlined
					class="text-none sync-btn"
					:loading="sincronizando"
					@click="confirmDialog = true"
					>
						<v-icon left small>mdi-sync</v-icon>
						Sincronizar
					</v-btn>

			</div>
		</v-card-title>

		<v-divider />

		<v-data-table
		:headers="headers"
		:items="bancoHoras"
		item-key="idfuncionarios"
		:loading="loading"
		:search="search"
		class="elevation-0 clean-table"
		show-expand
		single-expand
		:expanded.sync="expanded"
		disable-pagination
		hide-default-footer
		>
		<template #item.nome="{ item }">
			<div class="font-weight-medium primary-text">{{ item.nome }}</div>
			<div class="text-caption grey--text text--darken-1">
				{{ item.descricaoPeriodo || "Período calculado pelos expedientes" }}
			</div>
		</template>

		<template #item.variacaoPeriodoMin="{ item }">
			<div class="saldo-cell">
				<span class="font-weight-bold" :class="saldoClass(item.variacaoPeriodoMin)">
					{{ formatMinutes(item.variacaoPeriodoMin) }}
				</span>
				<div class="text-caption grey--text text--darken-1">expedientes</div>
			</div>
		</template>

		<template #item.saldoAcumuladoMin="{ item }">
			<div class="saldo-cell">
				<span class="font-weight-bold" :class="saldoClass(item.saldoAcumuladoMin)">
					{{ formatMinutes(item.saldoAcumuladoMin) }}
				</span>
				<div class="text-caption grey--text text--darken-1">
					{{ item.possuiSaldoBanco ? "saldo gravado" : "Sem saldo gravado" }}
				</div>
			</div>
		</template>

		<template #item.status="{ item }">
			<v-chip small :color="getStatus(item).color" text-color="white">
				<v-icon left x-small>{{ getStatus(item).icon }}</v-icon>
				{{ getStatus(item).text }}
			</v-chip>
		</template>

		<template #expanded-item="{ headers, item }">
			<td :colspan="headers.length" class="expanded-cell">
				<v-row dense>
					<v-col cols="12" md="7">
						<div class="section-panel">
							<div class="section-title">
								<v-icon small color="info" class="mr-1">mdi-calculator-variant-outline</v-icon>
								Calculo do periodo
							</div>
							<div class="section-note">Informativo calculado pelos expedientes do periodo selecionado.</div>

							<v-row dense>
								<v-col cols="6" sm="4">
									<Metric label="Trabalhadas" :value="formatMinutes(item.trabalhadoMin)" color-class="info--text" />
								</v-col>

								<v-col cols="6" sm="4">
									<Metric label="Previstas" :value="formatMinutes(item.previstoMin)" />
								</v-col>

								<v-col cols="6" sm="4">
									<Metric label="Abonadas" :value="formatMinutes(item.abonadoMin)" color-class="success--text text--darken-1" />
								</v-col>

								<v-col cols="6" sm="4">
									<Metric label="Esperadas" :value="formatMinutes(item.esperadoMin)" note="previstas - abonadas" />
								</v-col>

								<v-col cols="6" sm="4">
									<Metric label="Variacao do periodo" :value="formatMinutes(item.variacaoPeriodoMin)" :color-class="saldoClass(item.variacaoPeriodoMin)" note="trabalhadas - esperadas" />
								</v-col>
							</v-row>
						</div>
					</v-col>

					<v-col cols="12" md="5">
						<div class="section-panel">
							<div class="section-title">
								<v-icon small color="secondary" class="mr-1">mdi-database-outline</v-icon>
								Banco / auditoria
							</div>
							<div class="section-note">Somente o saldo lancado compoe o banco. Horas nao lancadas ficam separadas.</div>
							<v-alert
								v-if="item.auditoriaSaldo && (!item.auditoriaSaldo.reconciliado || !item.auditoriaSaldo.competenciaSincronizada)"
								type="warning"
								dense
								text
								class="mt-2 mb-2 body-2"
							>
								<span v-if="!item.auditoriaSaldo.reconciliado">Resumo contabil divergente em {{ formatMinutes(item.auditoriaSaldo.diferencaMinutos) }}. </span>
								<span v-if="!item.auditoriaSaldo.competenciaSincronizada">Competencia pendente de sincronizacao.</span>
							</v-alert>

							<div class="balance-flow">
								<div class="balance-step">
									<div class="balance-label">Saldo anterior</div>
									<div class="balance-value" :class="saldoClass(item.saldoAnteriorMin)">
										{{ formatMinutes(item.saldoAnteriorMin) }}
									</div>
								</div>

								<v-icon small color="grey" class="balance-arrow">mdi-plus</v-icon>

								<div class="balance-step">
									<div class="balance-label">Movimento lancado</div>
									<div class="balance-value" :class="saldoClass(item.saldoLancadoMin)">
										{{ formatMinutes(item.saldoLancadoMin) }}
									</div>
								</div>

								<v-icon small color="grey" class="balance-arrow">mdi-equal</v-icon>

								<div class="balance-step current">
								<div class="balance-label">Saldo no fechamento</div>
									<div class="balance-value" :class="saldoClass(item.saldoAcumuladoMin)">
										{{ formatMinutes(item.saldoAcumuladoMin) }}
									</div>
								</div>
							</div>

							<div v-if="!item.possuiLancamentosBanco" class="empty-state">
								<v-icon small color="grey" class="mr-1">mdi-information-outline</v-icon>
								<span>{{ mensagemSemLancamentos(item) }}</span>
							</div>

							<v-row v-else dense>
								<v-col cols="6">
									<Metric label="Credito lancado" :value="formatMinutes(item.creditoLancadoMin)" color-class="success--text text--darken-1" />
								</v-col>

								<v-col cols="6">
									<Metric label="Debito lancado" :value="formatMinutes(item.debitoLancadoMin)" color-class="error--text text--darken-1" />
								</v-col>

								<v-col cols="6">
									<Metric label="Saldo lancado no periodo" :value="formatMinutes(item.saldoLancadoMin)" :color-class="saldoClass(item.saldoLancadoMin)" />
								</v-col>

								<v-col cols="6">
									<Metric label="Horas nao lancadas" :value="formatMinutes(item.excedenteLancadoMin)" color-class="warning--text text--darken-2" note="excedente administrativo" />
								</v-col>
							</v-row>
						</div>

						<div v-if="!item.saldoHistoricoDisponivel" class="empty-state">
							<v-icon small color="warning" class="mr-1">mdi-alert-outline</v-icon>
							<span>Historico indisponivel antes do saldo de abertura auditavel.</span>
						</div>
					</v-col>
				</v-row>
			</td>
		</template>

		<template #no-data>
			<div class="pa-6 text-center secondary--text">Nenhum registro encontrado.</div>
		</template>
	</v-data-table>
</v-card>

<v-dialog v-model="confirmDialog" max-width="460">
	<v-card>
		<v-card-title class="text-subtitle-1 font-weight-medium">
			Sincronizar banco de horas?
		</v-card-title>

		<v-card-text class="body-2 grey--text text--darken-2">
			Esta ação pode criar ou atualizar lançamentos do banco de horas e impactar o saldo acumulado gravado.
		</v-card-text>

		<v-card-actions>
			<v-spacer />
			<v-btn text class="text-none" @click="confirmDialog = false">Cancelar</v-btn>
			<v-btn color="primary" class="text-none" @click="sincronizarBanco" :loading="sincronizando">
				Confirmar sincronização
			</v-btn>
		</v-card-actions>
	</v-card>
</v-dialog>

</v-container>
</template>

<script>
import axios from "axios";
import { baseApiUrl, showError } from "@/global";
import PageTitle from "@/templates/PageTitle";

const toNumber = (v) => {
	const n = Number(String(v ?? "").replace(",", "."));
	return Number.isFinite(n) ? n : 0;
};

const hasValue = (v) => v !== null && v !== undefined && v !== "";
const decimalToMinutes = (v) => Math.trunc(toNumber(v) * 60);
const normalizeFlag = (v) => v === true || v === 1 || v === "1" || v === "true";
const closedReferenceDate = () => {
	const date = new Date();
	date.setDate(date.getDate() - 1);
	return date;
};

export default {
	name: "BancoHoras",
	props: {
		embedded: { type: Boolean, default: false },
	},
	components: {
		PageTitle,
		Kpi: {
			name: "Kpi",
			props: {
				value: { type: [Number, String], required: true },
				label: { type: String, required: true },
				icon: { type: String, default: "mdi-information" },
				color: { type: String, default: "secondary" },
			},
			render(h) {
				return h("div", { class: "d-flex align-center" }, [
					h('i', {
						class: [
							'v-icon',
							'notranslate',
							'mdi',
							this.icon,
							`${this.color || 'primary'}--text`,
							'mr-3'
							],
						style: {
							fontSize: '30px',
							lineHeight: '1'
						},
						attrs: {
							'aria-hidden': 'true'
						}
					}),
					h("div", [
						h("div", { class: "text-h6 font-weight-regular" }, String(this.value)),
						h("div", { class: "text-caption secondary--text text--lighten-1" }, this.label),
						]),
					]);
			},
		},
		Metric: {
			name: "Metric",
			props: {
				label: { type: String, required: true },
				value: { type: String, required: true },
				note: { type: String, default: "" },
				colorClass: { type: String, default: "grey--text text--darken-2" },
			},
			render(h) {
				const children = [
					h("div", { class: "metric-label" }, this.label),
					h("div", { class: ["metric-value", this.colorClass] }, this.value),
					];

				if (this.note) {
					children.push(h("div", { class: "metric-note" }, this.note));
				}

				return h("div", { class: "metric-box" }, children);
			},
		},
	},

	data() {
		const referencia = closedReferenceDate();
		return {
			bancoHoras: [],
			loading: false,
			sincronizando: false,
			confirmDialog: false,
			search: "",
			expanded: [],
			mesSelecionado: referencia.getMonth() + 1,
			anoSelecionado: referencia.getFullYear(),
			meses: [
				{ nome: "Janeiro", numero: 1 },
				{ nome: "Fevereiro", numero: 2 },
				{ nome: "Marco", numero: 3 },
				{ nome: "Abril", numero: 4 },
				{ nome: "Maio", numero: 5 },
				{ nome: "Junho", numero: 6 },
				{ nome: "Julho", numero: 7 },
				{ nome: "Agosto", numero: 8 },
				{ nome: "Setembro", numero: 9 },
				{ nome: "Outubro", numero: 10 },
				{ nome: "Novembro", numero: 11 },
				{ nome: "Dezembro", numero: 12 },
			],
			anos: Array.from({ length: 5 }, (_, i) => referencia.getFullYear() - i),
			headers: [
				{ text: "Colaborador", value: "nome" },
				{ text: "Variacao do periodo", value: "variacaoPeriodoMin", align: "center" },
				{ text: "Saldo no fechamento", value: "saldoAcumuladoMin", align: "center" },
				{ text: "Situacao", value: "status", align: "center", sortable: false },
				{ text: "Detalhes", value: "data-table-expand", align: "center", sortable: false },
				],
		};
	},

	computed: {
		colaboradoresDebitoPeriodo() {
			return this.bancoHoras.filter((i) => i.variacaoPeriodoMin < 0).length;
		},

		colaboradoresCreditoPeriodo() {
			return this.bancoHoras.filter((i) => i.variacaoPeriodoMin > 0).length;
		},

		saldoAcumuladoGeralMin() {
			return this.bancoHoras.reduce((acc, item) => acc + item.saldoAcumuladoMin, 0);
		},
	},

	methods: {
		toNumber,

		periodoParams() {
			return {
				mes: this.mesSelecionado,
				ano: this.anoSelecionado,
				auditoria: 1,
			};
		},

		minutesFrom(item, minuteKeys, decimalKeys) {
			for (const key of minuteKeys) {
				if (hasValue(item[key])) return Math.trunc(toNumber(item[key]));
			}

			for (const key of decimalKeys) {
				if (hasValue(item[key])) return decimalToMinutes(item[key]);
			}

			return 0;
		},

		normalizeRow(item) {
			const possuiSaldoBanco = normalizeFlag(item.possuiSaldoBanco) || normalizeFlag(item.possuiDadosPersistidosBanco);
			const possuiLancamentosBanco = normalizeFlag(item.possuiLancamentosBanco) || normalizeFlag(item.temLancamentosBanco);

			const trabalhadoMin = this.minutesFrom(item, ["minutosTrabalhados", "trabalhadoMinutos", "trabalhadoMin"], ["horasTrabalhadas"]);
			const previstoMin = this.minutesFrom(item, ["minutosPrevistos", "previstoMinutos", "previstoMin"], ["horasPrevistas"]);
			const abonadoMin = this.minutesFrom(item, ["minutosAbonados", "abonadoMinutos", "abonadoMin"], ["horasAbonadas"]);
			const esperadoMin = this.minutesFrom(item, ["minutosEsperados", "esperadoMinutos", "esperadoMin"], ["horasEsperadas"]);

			const variacaoPeriodoMin = this.minutesFrom(
				item,
				["variacaoRelatorioMinutos", "variacaoPeriodoMinutos", "saldoMesMinutos", "deltaRelatorioMin"],
				["horasExtras", "saldoMes"]
				);

			const creditoLancadoMin = possuiLancamentosBanco
			? this.minutesFrom(item, ["creditoLancadoMinutos", "horasCreditadasMinutos"], ["horasCreditadas"])
			: 0;

			const debitoLancadoMin = possuiLancamentosBanco
			? this.minutesFrom(item, ["debitoLancadoMinutos", "horasDebitoMinutos"], ["horasDebito"])
			: 0;

			const saldoLancadoMin = possuiLancamentosBanco
			? this.minutesFrom(item, ["movimentoPeriodoMinutos", "saldoLancadoMinutos", "saldoLancadoMin"], ["saldoLancadoMes"])
			: 0;

			const excedenteLancadoMin = possuiLancamentosBanco
			? this.minutesFrom(item, ["excedenteNaoLancadoMinutos", "excedenteNaoLancadoAnoMinutos"], ["excedenteNaoLancado", "excedenteNaoLancadoAno"])
			: 0;

			const saldoAcumuladoMin = this.minutesFrom(
				item,
				["saldoFinalPeriodoMinutos", "saldoMinutos", "saldoAcumuladoMinutos"],
				["saldo"]
			);
			const saldoAnteriorMin = this.minutesFrom(item, ["saldoAnteriorMinutos"], []);

			return {
				...item,
				possuiSaldoBanco,
				possuiLancamentosBanco,
				trabalhadoMin,
				previstoMin,
				abonadoMin,
				esperadoMin,
				variacaoPeriodoMin,
				creditoLancadoMin,
				debitoLancadoMin,
				saldoLancadoMin,
				excedenteLancadoMin,
				saldoAcumuladoMin,
				saldoAnteriorMin,
				saldoAtualMin: this.minutesFrom(item, ["saldoAtualMinutos", "saldoMinutos"], ["saldo"]),
				descricaoPeriodo: item.descricaoPeriodo || null,
			};
		},

		async loadBancoHoras() {
			this.loading = true;
			try {
				const { data } = await axios.get(`${baseApiUrl}/bancohoras/relatorio/geral`, {
					params: this.periodoParams(),
				});
				this.bancoHoras = (data || []).map(this.normalizeRow);
			} catch (err) {
				showError(err);
			} finally {
				this.loading = false;
			}
		},

		async sincronizarBanco() {
			this.sincronizando = true;
			try {
				await axios.post(`${baseApiUrl}/bancohoras/sincronizar/todos`, null, {
					params: this.periodoParams(),
				});
				this.$toasted.success("Banco de horas sincronizado com sucesso!");
				this.confirmDialog = false;
				await this.loadBancoHoras();
			} catch (err) {
				showError(err);
			} finally {
				this.sincronizando = false;
			}
		},

		formatMinutes(totalMinutes) {
			const value = Math.trunc(toNumber(totalMinutes));
			const negative = value < 0;
			const abs = Math.abs(value);
			const hours = Math.floor(abs / 60);
			const minutes = abs % 60;
			const text = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
			return negative ? `-${text}` : text;
		},

		saldoClass(totalMinutes) {
			const v = toNumber(totalMinutes);
			if (v > 0) return "success--text";
			if (v < 0) return "error--text";
			return "grey--text text--darken-1";
		},

		mensagemSemLancamentos(item) {
			if (item.possuiSaldoBanco) {
				return "Ha saldo acumulado gravado, mas nao ha extrato detalhado de lancamentos para este periodo.";
			}

			return "Nao ha lancamentos gravados para este periodo.";
		},

		getStatus(item) {
			if (!item.possuiSaldoBanco) {
				return { color: "secondary", icon: "mdi-database-off-outline", text: "Sem saldo" };
			}

			const v = toNumber(item.saldoAcumuladoMin);
			if (v === 0) return { color: "success", icon: "mdi-check-circle", text: "Em dia" };
			if (v < 0) return { color: "error", icon: "mdi-alert-circle", text: "Devendo" };
			return { color: "success", icon: "mdi-trending-up", text: "Crédito" };
		},
	},

	mounted() {
		this.loadBancoHoras();
	},
};
</script>

<style scoped>
.summary-card,
.clean-table,
.main-card {
	background-color: #fafafa;
}

.summary-card,
.main-card {
	border: 1px solid #eeeeee;
}

.clean-alert {
	border-radius: 8px;
}

.table-title-wrap {
	gap: 12px;
}

.title-block {
	min-width: 260px;
}

.toolbar-actions {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

.sync-btn {
	min-width: 128px;
}

.table-search {
	max-width: 320px;
	min-width: 240px;
}

.period-select {
	max-width: 150px;
}

.year-select {
	max-width: 110px;
}

.primary-text {
	color: #37474f;
}

.saldo-cell {
	line-height: 1.2;
}

.expanded-cell {
	background: #fafafa;
	padding: 16px !important;
}

.section-panel {
	background: #fff;
	border: 1px solid #eeeeee;
	border-radius: 10px;
	padding: 14px;
	height: 100%;
}

.section-title {
	display: flex;
	align-items: center;
	font-size: 13px;
	font-weight: 600;
	color: #455a64;
	margin-bottom: 4px;
}

.section-note {
	color: #757575;
	font-size: 11px;
	line-height: 1.35;
	margin-bottom: 10px;
}

.balance-flow {
	display: grid;
	grid-template-columns: 1fr auto 1fr auto 1fr;
	align-items: stretch;
	gap: 8px;
	margin-bottom: 12px;
}

.balance-step {
	background: #fafafa;
	border: 1px solid #eeeeee;
	border-radius: 8px;
	padding: 10px;
	min-height: 70px;
}

.balance-step.current {
	background: #fff;
	border-color: #e0e0e0;
}

.balance-label {
	color: #757575;
	font-size: 11px;
	margin-bottom: 4px;
}

.balance-value {
	font-size: 17px;
	font-weight: 600;
	line-height: 1.2;
}

.balance-arrow {
	align-self: center;
}

.metric-box {
	background: #fafafa;
	border: 1px solid #f0f0f0;
	border-radius: 8px;
	padding: 10px;
	min-height: 76px;
}

.metric-label {
	font-size: 11px;
	color: #757575;
	margin-bottom: 3px;
}

.metric-value {
	font-size: 17px;
	font-weight: 600;
	line-height: 1.2;
}

.metric-note {
	font-size: 10px;
	color: #9e9e9e;
	margin-top: 3px;
}

.empty-state {
	background: #fafafa;
	border: 1px dashed #e0e0e0;
	border-radius: 8px;
	padding: 14px;
	font-size: 12px;
	color: #757575;
	display: flex;
	align-items: center;
}

@media print {
	.toolbar-actions,
	.clean-alert,
	.v-btn {
		display: none !important;
	}

	.main-card,
	.summary-card,
	.clean-table {
		background: #fff !important;
	}
}

@media (max-width: 960px) {
	.balance-flow {
		grid-template-columns: 1fr;
	}

	.balance-arrow {
		display: none;
	}
}
</style>
